import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'

import { useAuthStore } from '@/stores/auth'
import { useNoticeStore } from '@/stores/notice'
import { resetPageMeta } from '@/lib/seo'

declare module 'vue-router' {
  interface RouteMeta {
    layout?: 'public' | 'studio' | 'login'
    requiresAuth?: boolean
    requiresGuest?: boolean
    title?: string
  }
}

const routes: RouteRecordRaw[] = [
  { path: '/', name: 'home', component: () => import('@/views/public/HomePage.vue'), meta: { layout: 'public', title: '首页' } },
  { path: '/articles', name: 'articles', component: () => import('@/views/public/ArticleListPage.vue'), meta: { layout: 'public', title: '文章' } },
  { path: '/articles/:slug', name: 'article-detail', component: () => import('@/views/public/ArticleDetailPage.vue'), meta: { layout: 'public' } },
  { path: '/tools', name: 'tools', component: () => import('@/views/tools/ToolboxPage.vue'), meta: { layout: 'public', title: '工具箱' } },
  { path: '/tools/text', name: 'tool-text', component: () => import('@/views/tools/TextExtractorPage.vue'), meta: { layout: 'public', title: '文字提取器' } },
  { path: '/tools/css', name: 'tool-css', component: () => import('@/views/tools/CssToolPage.vue'), meta: { layout: 'public', title: 'CSS 工具' } },
  { path: '/tools/random', name: 'tool-random', component: () => import('@/views/tools/RandomStringPage.vue'), meta: { layout: 'public', title: '随机字符串' } },
  { path: '/tools/globe', name: 'tool-globe', component: () => import('@/views/tools/GlobePage.vue'), meta: { layout: 'public', title: '3D 地球' } },
  { path: '/tools/video', name: 'tool-video', component: () => import('@/views/tools/VideoPlayerPage.vue'), meta: { layout: 'public', title: '视频播放器' } },
  { path: '/tools/cheatsheet', name: 'tool-cheatsheet', component: () => import('@/views/tools/CheatsheetPage.vue'), meta: { layout: 'public', title: '速查手册' } },
  { path: '/studio/login', name: 'studio-login', component: () => import('@/views/studio/LoginPage.vue'), meta: { layout: 'login', requiresGuest: true, title: '编辑部登录' } },
  { path: '/studio', name: 'studio-dashboard', component: () => import('@/views/studio/DashboardPage.vue'), meta: { layout: 'studio', requiresAuth: true, title: '仪表盘' } },
  { path: '/studio/articles', name: 'studio-articles', component: () => import('@/views/studio/ArticleListPage.vue'), meta: { layout: 'studio', requiresAuth: true, title: '文章管理' } },
  { path: '/studio/articles/new', name: 'studio-article-new', component: () => import('@/views/studio/ArticleEditorPage.vue'), meta: { layout: 'studio', requiresAuth: true, title: '新建文章' } },
  { path: '/studio/articles/:id/edit', name: 'studio-article-edit', component: () => import('@/views/studio/ArticleEditorPage.vue'), meta: { layout: 'studio', requiresAuth: true, title: '编辑文章' } },
  { path: '/studio/taxonomy', name: 'studio-taxonomy', component: () => import('@/views/studio/TaxonomyPage.vue'), meta: { layout: 'studio', requiresAuth: true, title: '分类与标签' } },
  { path: '/studio/account', name: 'studio-account', component: () => import('@/views/studio/AccountPage.vue'), meta: { layout: 'studio', requiresAuth: true, title: '账户设置' } },
  { path: '/:pathMatch(.*)*', name: 'not-found', component: () => import('@/views/public/NotFoundPage.vue'), meta: { layout: 'public', title: '页面不存在' } },
]

export const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior: () => ({ top: 0 }),
})

router.beforeEach(async (to) => {
  const auth = useAuthStore()
  if ((to.meta.requiresAuth || to.meta.requiresGuest) && !auth.initialized && !auth.isAuthenticated) {
    try {
      await auth.restore()
    } catch (error) {
      useNoticeStore().show(error instanceof Error ? error.message : '登录状态读取失败', 'error')
    }
  }
  if (to.meta.requiresAuth && !auth.isAuthenticated) {
    return { name: 'studio-login', query: { redirect: to.fullPath } }
  }
  if (to.meta.requiresGuest && auth.isAuthenticated) {
    return { name: 'studio-dashboard' }
  }
  return true
})

router.afterEach((to, _from, failure) => {
  if (!failure) resetPageMeta(to.meta.title, to.path, Boolean(to.meta.requiresAuth || to.meta.requiresGuest))
})
